import { useState, useMemo, useRef } from 'react';
import {
  Box, Typography, Chip,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import TagChips from '../../components/TagChips';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { landingPagesService } from '../../services/landing-pages.service';
import { useModalState } from '../../hooks/useModalState';
import { useSnackbar } from 'notistack';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import TagSelect from '../../components/TagSelect';
import BulkEditDialog, { BulkFieldDef } from '../../components/BulkEditDialog';
import { resolveSelectedIds, emptySelection, buildEntityWhere, applyBulkUpdate, BulkResult } from '../../utils/entity-list';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import ActivityCell, { activityOrderBy } from '../../components/ActivityCell';

const BULK_FIELDS: BulkFieldDef[] = [
  { key: 'tag_ids', label: 'Tags', type: 'tags' },
  { key: 'status', label: 'Status', type: 'status' },
];

export default function LandingPagesPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('landing-page');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [filterTagIds, setFilterTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('landing_page', { search: debouncedSearch, statuses, tagIds: filterTagIds }, ['name', 'url']),
    [debouncedSearch, statuses, filterTagIds],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);
  const [sort, setSort] = useState<GridSortModel>([{ field: '_activity', sort: 'desc' }]);
  const orderBy = activityOrderBy(sort);


  const { data, isFetching } = useQuery({
    queryKey: ['landing-pages', where, offset, limit, sort],
    queryFn: () => landingPagesService.query({ where, offset, limit, ...(orderBy && { order_by: orderBy, order: sort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }) }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);

  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
  const bulkAction = async (fn: () => Promise<unknown>, okMsg: string) => {
    try { await fn(); invalidate(); setSelection(emptySelection); enqueueSnackbar(okMsg, { variant: 'success' }); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : 'Action failed', { variant: 'error' }); }
  };
  const archiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => landingPagesService.delete(id))), `Archived ${selectedIds.length}`);
  const unarchiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => landingPagesService.unarchive(id))), `Unarchived ${selectedIds.length}`);
  const runBulk = async (result: BulkResult) => {
    setBulkLoading(true);
    try {
      await applyBulkUpdate((id, patch) => landingPagesService.update(id, patch), selectedIds, rows, result);
      invalidate(); setSelection(emptySelection); setBulkOpen(false);
      enqueueSnackbar(`Updated ${selectedIds.length} ${selectedIds.length === 1 ? 'item' : 'items'}`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Bulk update failed', { variant: 'error' });
    } finally { setBulkLoading(false); }
  };

  const columns: GridColDef[] = [
    { field: 'ref', headerName: 'Ref', width: 90, sortable: false, valueGetter: (_v, row) => row.ref || '—' },
    { field: 'name', headerName: 'Landing Page', flex: 1 },
    { field: 'url', headerName: 'URL', flex: 1, renderCell: (p: GridRenderCellParams) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Typography variant="body2" noWrap title={p.value}>{p.value}</Typography></Box>
    )},
    {
      field: '_activity', headerName: 'Activity', width: 150, filterable: false,
      description: 'Non-bot visits in the last hour and the last 24 hours. Sorts by the last 24 hours.',
      renderCell: (p: GridRenderCellParams) => (
        <ActivityCell
          activity={p.row.activity_status} senderLabel="Flows serving it"
          kind="landing_page" entityId={p.row.id} updatedAt={p.row.activity_updated_at}
        />
      ),
    },
    { field: 'status', headerName: 'Status', width: 100, renderCell: (p: GridRenderCellParams) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Chip label={p.value} size="small" color={p.value === 'active' ? 'success' : p.value === 'archived' ? 'warning' : 'default'} /></Box>
    )},
    { field: 'tag_ids', headerName: 'Tags', flex: 1, minWidth: 150, sortable: false, renderCell: (p: GridRenderCellParams) => <TagChips ids={p.value ?? []} /> },
    { field: 'created_at', headerName: 'Created', width: 110, valueFormatter: (v) => new Date(v).toLocaleDateString() },
    {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate' })} title="Duplicate">
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Landing Pages</Typography>
      </Box>

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        sortingMode="server"
        sortModel={sort}
        onSortModelChange={setSort}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          filters: <TagSelect value={filterTagIds} onChange={setFilterTagIds} label="Filter by tags" allowCreate={false} sx={{ minWidth: 200 }} />,
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'Create landing page', onClick: () => setModal({ open: true, reason: 'Create' }) }],
          selectedCount: selectedIds.length,
          pluralName: 'landing pages',
          selectionActions: [
            { icon: Edit, hint: 'Bulk edit', onClick: () => setBulkOpen(true), color: 'inherit' as const },
            { icon: VisibilityOff, hint: 'Archive', onClick: archiveSelected, color: 'inherit' as const },
            { icon: Visibility, hint: 'Unarchive', onClick: unarchiveSelected, color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        keepNonExistentRowsSelected
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <BulkEditDialog
        open={bulkOpen}
        count={selectedIds.length}
        fields={BULK_FIELDS}
        loading={bulkLoading}
        onClose={() => setBulkOpen(false)}
        onApply={runBulk}
      />
    </Box>
  );
}
